import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calendar, Clock, Users, CheckCircle, XCircle, Plus, Loader2, FileText, ChevronRight, Briefcase, Timer, ArrowUp, AlertTriangle, Info, Table } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { TestimonialCard } from "@/components/TestimonialCard";
import { ProLightPromoCard } from "@/components/ProLightPromoCard";

// Helper function to format date from YYYY-MM-DD to DD-MM-YYYY (Italian format)
const formatDateIT = (dateStr) => {
  if (!dateStr) return "";
  try {
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
};

// Helper function to check if user has manager-level visibility
const isManagerRole = (ruolo) => ruolo === "titolare" || ruolo === "amministrativo";

export default function DashboardPage() {
  const { user, token, settings, refreshSettings } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [personalStats, setPersonalStats] = useState(null);
  const [allRequests, setAllRequests] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [employeesOverview, setEmployeesOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteCode, setInviteCode] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [newRequestOpen, setNewRequestOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  const [loadMoreCount, setLoadMoreCount] = useState(5);
  const [newRequest, setNewRequest] = useState({
    tipo: "ferie",
    data_inizio: "",
    data_fine: "",
    ora_inizio: "",
    ora_fine: "",
    note: ""
  });

  useEffect(() => {
    fetchData();
    // Fetch invite code separately so trial-expired errors don't block it
    if (user?.ruolo === "titolare" && token) {
      axios.get(`${API}/company/invite-code`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setInviteCode(res.data.codice_invito || ""))
        .catch(() => {});
    }
  }, [token, user?.id, user?.ruolo]);

  // Poll subscription status every 30s for the Titolare so we can show
  // the tier-change banner in (near) real time when a new employee registers.
  useEffect(() => {
    if (user?.ruolo !== "titolare" || !token) return;
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API}/subscription/status`, { headers: { Authorization: `Bearer ${token}` } });
        setSubscriptionStatus(res.data);
      } catch (e) {
        // ignore — fail silently
      }
    };
    fetchStatus();
    const id = setInterval(fetchStatus, 30000);
    return () => clearInterval(id);
  }, [token, user?.ruolo]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, requestsRes, personalStatsRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers }),
        axios.get(`${API}/leave-requests`, { headers }),
        axios.get(`${API}/stats/personal`, { headers })
      ]);
      setStats(statsRes.data);
      setAllRequests(requestsRes.data);
      setRecentRequests(requestsRes.data.slice(0, 5));
      setVisibleCount(5);
      setPersonalStats(personalStatsRes.data);
      
      // Fetch leave balance for current user
      try {
        const balanceRes = await axios.get(`${API}/users/${user?.id}/leave-balance`, { headers });
        setLeaveBalance(balanceRes.data);
      } catch (e) {
        console.log("Leave balance not available");
      }
      
      if (isManagerRole(user?.ruolo)) {
        const pendingRes = await axios.get(`${API}/leave-requests/pending`, { headers });
        setPendingRequests(pendingRes.data);
        
        // Fetch employees overview for titolare
        if (user?.ruolo === "titolare") {
          try {
            const overviewRes = await axios.get(`${API}/employees-overview`, { headers });
            setEmployeesOverview(overviewRes.data);
          } catch (e) {
            console.log("Employees overview not available");
          }
          
          // Fetch invite code (used in Base plan card and elsewhere)
          try {
            const inviteRes = await axios.get(`${API}/company/invite-code`, { headers });
            setInviteCode(inviteRes.data.codice_invito || "");
          } catch (e) {
            console.log("Invite code not available");
          }
        }
      }
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leave-requests`, newRequest, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Richiesta inviata con successo!");
      setNewRequestOpen(false);
      setNewRequest({ tipo: "ferie", data_inizio: "", data_fine: "", note: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio della richiesta");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoadMore = () => {
    const newCount = Math.min(visibleCount + loadMoreCount, allRequests.length);
    setVisibleCount(newCount);
    setRecentRequests(allRequests.slice(0, newCount));
  };

  const remainingCount = allRequests.length - visibleCount;

  const isBasePlan = (settings?.piano || "base") === "base";
  const isEnterprisePlan = (settings?.piano || "base") === "enterprise";
  const showInviteCard = user?.ruolo === "titolare" && isBasePlan && inviteCode;
  
  // Tier banner uses backend-computed values (tier_price, tier_previous_price)
  // so that frontend and backend never drift if pricing changes in the future.
  const ITALIAN_ORDINALS = {
    6: "sesto", 7: "settimo", 8: "ottavo", 9: "nono", 10: "decimo",
    11: "undicesimo", 12: "dodicesimo", 13: "tredicesimo", 14: "quattordicesimo", 15: "quindicesimo",
    16: "sedicesimo", 17: "diciassettesimo", 18: "diciottesimo", 19: "diciannovesimo", 20: "ventesimo",
    21: "ventunesimo", 25: "venticinquesimo", 30: "trentesimo", 31: "trentunesimo", 32: "trentaduesimo"
  };
  const formatPriceIT = (n) => Number(n).toFixed(2).replace('.', ',');

  // Tier change banner — shown to Titolare when an employee registration crossed a price threshold
  const tierBannerInfo = (() => {
    if (user?.ruolo !== "titolare" || !subscriptionStatus) return null;
    const n = subscriptionStatus.current_employees || 0;
    const prev = subscriptionStatus.tier_previous_price;
    const curr = subscriptionStatus.tier_price;
    if (prev === null || prev === undefined || curr === null || curr === undefined || curr <= prev) return null;
    const ordinale = ITALIAN_ORDINALS[n] || `${n}°`;
    const dismissKey = `tier_banner_dismissed_${user.id}_${n}`;
    if (typeof window !== "undefined" && localStorage.getItem(dismissKey) === "1") return null;
    return { n, prev, curr, ordinale, dismissKey };
  })();

  const dismissTierBanner = () => {
    if (tierBannerInfo) {
      localStorage.setItem(tierBannerInfo.dismissKey, "1");
      // Force re-render by tweaking subscription status (set the same value)
      setSubscriptionStatus({ ...subscriptionStatus });
    }
  };

  // First-access detection: snapshot at mount so the layout doesn't flip mid-session
  const inviteCardSeenKey = user?.id ? `invite_card_seen_${user.id}` : null;
  const [isFirstAccess, setIsFirstAccess] = useState(false);

  useEffect(() => {
    if (!showInviteCard || !inviteCardSeenKey) return;
    const alreadySeen = !!localStorage.getItem(inviteCardSeenKey);
    if (!alreadySeen) {
      setIsFirstAccess(true);
      localStorage.setItem(inviteCardSeenKey, "1");
    }
  }, [showInviteCard, inviteCardSeenKey]);

  const handleUpdateRequest = async (requestId, stato, note = "") => {
    try {
      await axios.put(`${API}/leave-requests/${requestId}`, { stato, note_risposta: note }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Richiesta ${stato === "approvato" ? "approvata" : "rifiutata"}!`);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento della richiesta");
    }
  };

  // Reject reason dialog
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const openRejectDialog = (request) => {
    setRejectTarget(request);
    setRejectReason("");
  };

  const handleConfirmReject = async () => {
    if (!rejectTarget) return;
    setRejecting(true);
    try {
      await axios.put(`${API}/leave-requests/${rejectTarget.id}`, { stato: "rifiutato", note_risposta: rejectReason.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Richiesta rifiutata!");
      setRejectTarget(null);
      setRejectReason("");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento della richiesta");
    } finally {
      setRejecting(false);
    }
  };

  const getStatusBadge = (stato) => {
    switch (stato) {
      case "in_attesa":
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">In attesa</Badge>;
      case "approvato":
        return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Approvato</Badge>;
      case "rifiutato":
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Rifiutato</Badge>;
      default:
        return <Badge>{stato}</Badge>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight" data-testid="dashboard-title">
              Ciao, {user?.nome}!
            </h1>
            <p className="text-slate-500 mt-1">
              {user?.ruolo === "titolare" ? "Visualizza le richieste del team" : "Gestisci le tue ferie e permessi"}
            </p>
          </div>
          <div className={user?.ruolo === "dipendente" || user?.ruolo === "amministrativo" ? "" : "hidden"}>
            <Dialog open={newRequestOpen} onOpenChange={setNewRequestOpen}>
              <DialogTrigger asChild>
                <Button className="btn-theme btn-press" data-testid="new-request-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Invia una richiesta di ferie o permesso
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="leave-request-dialog">
                <DialogHeader>
                  <DialogTitle>Nuova Richiesta</DialogTitle>
                  <DialogDescription>Compila i campi per inviare una richiesta di ferie o permesso.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateRequest} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select value={newRequest.tipo} onValueChange={(v) => setNewRequest({ ...newRequest, tipo: v, data_fine: v === "permesso" ? "" : newRequest.data_fine })}>
                      <SelectTrigger data-testid="request-tipo-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ferie">Ferie</SelectItem>
                        <SelectItem value="permesso">Permesso</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Date fields - different for ferie vs permesso */}
                  {newRequest.tipo === "ferie" ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Data inizio</Label>
                        <Input
                          type="date"
                          value={newRequest.data_inizio}
                          onChange={(e) => setNewRequest({ ...newRequest, data_inizio: e.target.value })}
                          required
                          data-testid="request-data-inizio"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Data fine</Label>
                        <Input
                          type="date"
                          value={newRequest.data_fine}
                          onChange={(e) => setNewRequest({ ...newRequest, data_fine: e.target.value })}
                          required
                          data-testid="request-data-fine"
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label>Data</Label>
                        <Input
                          type="date"
                          value={newRequest.data_inizio}
                          onChange={(e) => setNewRequest({ ...newRequest, data_inizio: e.target.value, data_fine: e.target.value })}
                          required
                          data-testid="request-data-inizio"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Dalle ore</Label>
                          <Input
                            type="time"
                            value={newRequest.ora_inizio}
                            onChange={(e) => setNewRequest({ ...newRequest, ora_inizio: e.target.value })}
                            required
                            data-testid="request-ora-inizio"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Alle ore</Label>
                          <Input
                            type="time"
                            value={newRequest.ora_fine}
                            onChange={(e) => setNewRequest({ ...newRequest, ora_fine: e.target.value })}
                            required
                            data-testid="request-ora-fine"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  
                  <div className="space-y-2">
                    <Label>Note (opzionale)</Label>
                    <Textarea
                      placeholder="Aggiungi una nota..."
                      value={newRequest.note}
                      onChange={(e) => setNewRequest({ ...newRequest, note: e.target.value })}
                      data-testid="request-note"
                    />
                  </div>
                  <Button type="submit" className="w-full btn-theme" disabled={submitting} data-testid="submit-request-btn">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Invia Richiesta
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Tier change banner (Titolare only) — shown when a new employee registration crossed a price threshold */}
        {tierBannerInfo && (
          <Card className="border-2 border-amber-300 bg-amber-50 shadow-md" data-testid="tier-change-banner">
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0 bg-amber-100">
                  <AlertTriangle className="h-5 w-5 text-amber-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-amber-900">
                    Si è registrato il {tierBannerInfo.ordinale} dipendente
                  </p>
                  <p className="text-sm text-amber-800 mt-1 leading-relaxed">
                    La prossima scadenza mensile sarà <strong>€{formatPriceIT(tierBannerInfo.curr)}</strong>. Il piano resta invariato — cambia solo la fascia mensile in base al numero di dipendenti.
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Chiudi"
                  className="shrink-0 text-amber-700 hover:text-amber-900 transition-colors"
                  onClick={dismissTierBanner}
                  data-testid="dismiss-tier-banner-btn"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Base Plan: Codice Azienda card for Titolare */}
        {showInviteCard && isFirstAccess && (
          <Card className="border-2 border-theme-200 bg-gradient-to-br from-white to-theme-50/50 shadow-md" data-testid="base-plan-invite-card">
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col gap-5">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(to bottom right, var(--theme-500), var(--theme-600))' }}>
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Invita i tuoi dipendenti</h2>
                    <p className="text-sm text-slate-600 mt-1">
                      Condividi questo codice con i tuoi dipendenti — lo useranno per registrarsi all'app e unirsi alla tua azienda.
                    </p>
                  </div>
                </div>

                <div className="rounded-xl py-8 sm:py-10 px-6 flex flex-col items-center justify-center border-2 border-dashed gap-2" style={{ backgroundColor: 'var(--theme-50)', borderColor: 'var(--theme-300)' }}>
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--theme-600)' }}>Codice</span>
                  <span className="font-mono text-5xl sm:text-7xl lg:text-8xl font-extrabold tracking-[0.3em] sm:tracking-[0.4em]" style={{ color: 'var(--theme-700)' }} data-testid="invite-code-base-display">
                    {inviteCode}
                  </span>
                </div>

                <div className="rounded-xl p-5 sm:p-6 border-2" style={{ backgroundColor: 'var(--theme-50)', borderColor: 'var(--theme-200)' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Info className="h-5 w-5" style={{ color: 'var(--theme-600)' }} />
                    <h3 className="text-lg font-bold" style={{ color: 'var(--theme-700)' }}>Come funziona</h3>
                  </div>
                  <p className="text-base sm:text-lg leading-relaxed text-slate-800">
                    Fai scaricare l'app ai tuoi dipendenti e chiedi loro di registrarsi come dipendenti utilizzando questo codice.
                    <br />
                    Dal menu laterale potrai visualizzare tutte le richieste di ferie e permessi e approvarle o rifiutarle con un semplice click.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Base Plan: Compact invite reminder for subsequent accesses */}
        {showInviteCard && !isFirstAccess && (
          <Card className="border-slate-200 bg-white" data-testid="base-plan-invite-card-compact">
            <CardContent className="p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: 'var(--theme-100)' }}>
                    <Users className="h-5 w-5" style={{ color: 'var(--theme-600)' }} />
                  </div>
                  <div className="flex-1 min-w-0 text-sm leading-relaxed text-slate-700">
                    <p className="font-bold text-slate-900">Vuoi registrare un altro dipendente?</p>
                    <p>Il codice e la procedura sono sempre gli stessi.</p>
                    <p className="mt-1">Fai scaricare ai tuoi dipendenti l'app, che si registreranno come dipendenti inserendo questo codice.</p>
                  </div>
                </div>
                <div className="rounded-lg py-3 px-5 border-2 border-dashed shrink-0 self-center sm:self-auto" style={{ backgroundColor: 'var(--theme-50)', borderColor: 'var(--theme-300)' }}>
                  <span className="font-mono text-2xl sm:text-3xl font-bold tracking-[0.3em]" style={{ color: 'var(--theme-700)' }} data-testid="invite-code-base-compact">
                    {inviteCode}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Card raccolta testimonianze (titolare/amministrativo, dal 3° dipendente registrato) */}
        <TestimonialCard />

        {/* Promemoria Pro Light per aziende Free V2 (solo titolare, non su app iOS) */}
        {user?.ruolo === "titolare" && <ProLightPromoCard status={subscriptionStatus} />}

        <div className={(user?.ruolo === "dipendente" || user?.ruolo === "amministrativo") && settings?.mostra_saldi_dipendenti !== false ? "" : "hidden"}>
          {personalStats && (
            <Card className="border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100" data-testid="personal-stats-summary">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold text-slate-700">
                  La Mia Situazione Ferie e Permessi {personalStats.anno}
                </CardTitle>
                <CardDescription>Il tuo riepilogo personale di ferie e permessi quest'anno</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Leave Balance Section */}
                {leaveBalance && (
                  <div className={`grid grid-cols-1 ${leaveBalance.mostra_permessi_dipendente !== false ? 'md:grid-cols-2' : ''} gap-4`}>
                    {/* Ferie Card */}
                    <div className="p-4 bg-white rounded-lg border border-slate-200">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                          <Briefcase className="h-4 w-4 text-emerald-600" />
                        </div>
                        <h4 className="font-semibold text-slate-700">{settings?.ferie_a_ore_enabled ? "Ferie (ore)" : "Ferie (giorni)"}</h4>
                      </div>
                      {settings?.ferie_a_ore_enabled ? (
                        <div className="grid grid-cols-2 gap-3 text-sm" data-testid="ferie-ore-balance-card">
                          <div>
                            <p className="text-slate-500">Totale annuo</p>
                            <p className="font-bold text-slate-900">{leaveBalance.ferie_ore_totali_annue || 0}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Riporto {personalStats.anno - 1}</p>
                            <p className="font-bold text-slate-900">{leaveBalance.ferie_ore_riporto || 0}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Usate</p>
                            <p className="font-bold text-red-600">{((leaveBalance.ferie_ore_godute || 0) + (leaveBalance.ferie_ore_usate || 0)).toFixed(1)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Disponibili</p>
                            <p className="font-bold text-emerald-600 text-lg">{leaveBalance.ferie_ore_disponibili || 0}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-slate-500">Totale annuo</p>
                            <p className="font-bold text-slate-900">{leaveBalance.ferie_totali_annue}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Riporto {personalStats.anno - 1}</p>
                            <p className="font-bold text-slate-900">{leaveBalance.ferie_riporto}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Usate</p>
                            <p className="font-bold text-red-600">{leaveBalance.ferie_usate}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Disponibili</p>
                            <p className="font-bold text-emerald-600 text-lg">{leaveBalance.ferie_disponibili}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Permessi Card — hidden when titolare disabled it for this employee */}
                    {leaveBalance.mostra_permessi_dipendente !== false && (
                    <div className="p-4 bg-white rounded-lg border border-slate-200">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <Timer className="h-4 w-4 text-amber-600" />
                        </div>
                        <h4 className="font-semibold text-slate-700">Permessi ROL (ore)</h4>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-slate-500">Totale annuo</p>
                          <p className="font-bold text-slate-900">{leaveBalance.permessi_totali_annui}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Riporto {personalStats.anno - 1}</p>
                          <p className="font-bold text-slate-900">{leaveBalance.permessi_riporto}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Usati</p>
                          <p className="font-bold text-red-600">{leaveBalance.permessi_usati}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Disponibili</p>
                          <p className="font-bold text-amber-600 text-lg">{leaveBalance.permessi_disponibili}</p>
                        </div>
                      </div>
                    </div>
                    )}
                  </div>
                )}
                
                {/* In Attesa Section */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200">
                    <div className="p-2 bg-theme-100 rounded-lg">
                      <Clock className="h-5 w-5 text-theme-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{personalStats.ferie_in_attesa}</p>
                      <p className="text-xs text-slate-500">Ferie in attesa</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Clock className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{personalStats.permesso_in_attesa}</p>
                      <p className="text-xs text-slate-500">Permessi in attesa</p>
                    </div>
                  </div>
                </div>
                
                {/* Disclaimer */}
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-xs text-amber-800 space-y-1">
                      <p><strong>Nota importante:</strong></p>
                      <p>I dati visualizzati sono puramente indicativi e potrebbero non rispecchiare la situazione reale.</p>
                      <p>Non hanno alcun valore legale e non possono essere utilizzati per fini ufficiali.</p>
                      <p>Per i dati certificati fare sempre riferimento alla busta paga o alla documentazione fornita dall'azienda.</p>
                      <p>Da tali informazioni non deriva alcun diritto né obbligo per nessuna delle parti.</p>
                      <p>In caso di anomalie o possibili errori, il dipendente è invitato a segnalarli all'ufficio amministrativo o al titolare.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Stats Cards - Only for Titolare. On Base plan show only "Richieste in Attesa" full-width */}
        <div className={user?.ruolo === "titolare" ? `grid grid-cols-1 ${isBasePlan ? "" : "md:grid-cols-2"} gap-6` : "hidden"}>
            <>
              {/* Richieste in Attesa - Clickable only for Titolare */}
              <Card 
                className={`card-hover border-0 shadow-sm bg-white/80 backdrop-blur-sm transition-all duration-300 ${
                  user?.ruolo === "titolare" 
                    ? "cursor-pointer hover:shadow-lg hover:scale-[1.02] border-2 border-amber-200" 
                    : "hover:shadow-md"
                }`}
                data-testid="stat-pending"
                onClick={() => user?.ruolo === "titolare" && navigate("/requests?filter=in_attesa")}
              >
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500">Richieste in Attesa</CardTitle>
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Clock className="h-5 w-5 text-amber-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold text-slate-900">{stats?.pending_requests || 0}</div>
                    {user?.ruolo === "titolare" && (
                      <ChevronRight className="h-5 w-5 text-amber-500" />
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {user?.ruolo === "titolare" ? "Clicca per approvare o rifiutare" : "Da approvare"}
                  </p>
                </CardContent>
              </Card>
              {/* Total Employees - Clickable to go to employees page (hidden on Base plan) */}
              {!isBasePlan && (
                <Card 
                  className="card-hover border-0 shadow-sm bg-white/80 backdrop-blur-sm hover:shadow-md transition-all duration-300 cursor-pointer" 
                  data-testid="stat-employees"
                  onClick={() => navigate("/users")}
                >
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500">Totale Dipendenti</CardTitle>
                    <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--theme-100)' }}>
                      <Users className="h-5 w-5" style={{ color: 'var(--theme-600)' }} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-slate-900">{stats?.total_employees || 0}</div>
                    <p className="text-xs text-slate-400 mt-1">Nel team - Clicca per gestire</p>
                  </CardContent>
                </Card>
              )}
            </>
        </div>

        {/* Pending Requests list — kept for amministrativo (with gestione_richieste). For titolare it's removed: the clickable "Richieste in Attesa" card above already lets them jump to the full list. */}
        {user?.ruolo === "amministrativo" && user?.permessi_admin?.gestione_ferie_permessi && pendingRequests.length > 0 && (
          <Card className="border-0 shadow-sm bg-white/80 backdrop-blur-sm" data-testid="pending-requests-section">
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
                Richieste in Attesa
              </CardTitle>
              <CardDescription>
                Gestisci le richieste in attesa dei dipendenti
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pendingRequests.map((request, index) => (
                  <div
                    key={request.id}
                    className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-slate-100/50 rounded-xl border border-slate-100 gap-4 animate-fade-in stagger-${index + 1} hover:shadow-sm transition-all duration-200`}
                    data-testid={`pending-request-${request.id}`}
                  >
                    <div className="space-y-1">
                      <p className="font-medium text-slate-900">
                        {request.user_nome} {request.user_cognome}
                        {request.user_posizione && (
                          <span className="ml-2 text-sm font-normal text-slate-500">({request.user_posizione})</span>
                        )}
                      </p>
                      <p className="text-sm text-slate-600">
                        {request.tipo === "ferie" ? (
                          <>Ferie • {formatDateIT(request.data_inizio)} - {formatDateIT(request.data_fine)}</>
                        ) : (
                          <>Permesso • {formatDateIT(request.data_inizio)} {request.ora_inizio && request.ora_fine ? `dalle ${request.ora_inizio} alle ${request.ora_fine}` : ""}</>
                        )}
                      </p>
                      {request.note && <p className="text-sm text-slate-400 italic">"{request.note}"</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleUpdateRequest(request.id, "approvato")}
                        className="bg-emerald-600 hover:bg-emerald-700 btn-press shadow-sm"
                        data-testid={`approve-btn-${request.id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Approva
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openRejectDialog(request)}
                        className="border-red-200 text-red-600 hover:bg-red-50 btn-press"
                        data-testid={`reject-btn-${request.id}`}
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Rifiuta
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Requests - Different behavior based on role */}

        {/* Prospetto Ferie Dipendenti — solo per titolare Enterprise */}
        {user?.ruolo === "titolare" && isEnterprisePlan && settings?.mostra_prospetto_ferie !== false && (
          <Card className="border-slate-200" data-testid="employees-overview-section">
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <Table className="h-5 w-5 text-theme-600" />
                Prospetto Ferie Dipendenti
              </CardTitle>
              <CardDescription>
                Situazione completa delle ferie residue per ogni dipendente.
                <div className="mt-2 text-slate-500 text-sm">
                  Per un riepilogo accurato, configura le ferie/permessi del singolo dipendente da{" "}
                  <span 
                    onClick={() => navigate("/users")}
                    className="text-theme-600 hover:text-theme-700 cursor-pointer underline font-medium"
                  >
                    Elenco Utenti
                  </span>.
                </div>
                <div className="mt-1 text-slate-500 text-sm">
                  Vuoi mostrare il prospetto personale anche ai tuoi dipendenti?{" "}
                  <span 
                    onClick={() => navigate("/settings?tab=visibility")}
                    className="text-theme-600 hover:text-theme-700 cursor-pointer underline font-medium"
                  >
                    Configura la visibilità
                  </span>.
                </div>
              </CardDescription>
            </CardHeader>
            <CardContent>
              {employeesOverview.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>Nessun dipendente trovato</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left p-3 font-semibold text-slate-700 bg-slate-50">Dipendente</th>
                        <th className="text-center p-3 font-semibold text-emerald-700 bg-emerald-50">{settings?.ferie_a_ore_enabled ? "Ferie approvate (ore)" : "Ferie approvate (giorni)"}</th>
                        <th className="text-center p-3 font-semibold text-emerald-700 bg-emerald-50">{settings?.ferie_a_ore_enabled ? "Ferie residue (ore)" : "Ferie residue"}</th>
                        <th className="text-center p-3 font-semibold text-amber-700 bg-amber-50">Permessi approvati (ore)</th>
                        <th className="text-center p-3 font-semibold text-amber-700 bg-amber-50">Ore residue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeesOverview.map((emp) => {
                        const showPermessi = emp.mostra_permessi_dipendente !== false;
                        return (
                        <tr 
                          key={emp.user_id} 
                          className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                          data-testid={`employee-row-${emp.user_id}`}
                        >
                          <td className="p-3 bg-white">
                            <div>
                              <p className="font-medium text-slate-900">{emp.cognome} {emp.nome}</p>
                              {emp.posizione && <p className="text-xs text-slate-500">{emp.posizione}</p>}
                            </div>
                          </td>
                          <td className="text-center p-3 font-medium text-slate-700 bg-emerald-50/50">{settings?.ferie_a_ore_enabled ? emp.ferie_ore_approvate : emp.ferie_approvate_giorni}</td>
                          <td className="text-center p-3 bg-emerald-50/50">
                            {settings?.ferie_a_ore_enabled ? (
                              <span className={`font-bold ${emp.ferie_ore_residue > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {emp.ferie_ore_residue}
                              </span>
                            ) : (
                              <span className={`font-bold ${emp.ferie_residue > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {emp.ferie_residue}
                              </span>
                            )}
                          </td>
                          <td className="text-center p-3 font-medium text-slate-700 bg-amber-50/50">
                            {showPermessi ? emp.permessi_approvati_ore : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="text-center p-3 bg-amber-50/50">
                            {showPermessi ? (
                              <span className={`font-bold ${emp.ore_residue > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                                {emp.ore_residue}
                              </span>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Recent Requests - For dipendente and amministrativo only */}
        {user?.ruolo !== "titolare" && (
          <Card className="border-slate-200" data-testid="recent-requests-section">
            <CardHeader>
              <CardTitle className="text-xl">Le Mie Richieste</CardTitle>
              <CardDescription>Storico delle tue richieste</CardDescription>
            </CardHeader>
            <CardContent>
              {recentRequests.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>Nessuna richiesta trovata</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentRequests.map((request, index) => (
                    <div
                      key={request.id}
                      className={`flex items-center justify-between p-4 bg-slate-50 rounded-lg animate-fade-in stagger-${index + 1}`}
                      data-testid={`recent-request-${request.id}`}
                    >
                      <div className="space-y-1">
                        <p className="text-sm text-slate-600">
                          {request.tipo === "ferie" ? (
                            <>Ferie • {formatDateIT(request.data_inizio)} - {formatDateIT(request.data_fine)}</>
                          ) : (
                            <>Permesso • {formatDateIT(request.data_inizio)} {request.ora_inizio && request.ora_fine ? `dalle ${request.ora_inizio} alle ${request.ora_fine}` : ""}</>
                          )}
                        </p>
                      </div>
                      {getStatusBadge(request.stato)}
                    </div>
                  ))}
                  
                  {/* Load More Section */}
                  {remainingCount > 0 && (
                    <div className="flex items-center justify-center gap-3 pt-4 border-t border-slate-200 mt-4">
                      <span className="text-sm text-slate-500">Visualizza altre</span>
                      <Select
                        value={loadMoreCount.toString()}
                        onValueChange={(value) => setLoadMoreCount(parseInt(value))}
                      >
                        <SelectTrigger className="w-20 h-8" data-testid="load-more-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 5, 10, 15, 20, 25, 50, 99].map((n) => (
                            <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleLoadMore}
                        className="gap-2"
                        data-testid="load-more-btn"
                      >
                        Carica ({remainingCount} rimanenti)
                      </Button>
                    </div>
                  )}
                  
                  {/* Back to Top Button - shown when all items loaded and list is long */}
                  {remainingCount <= 0 && recentRequests.length > 5 && (
                    <div className="flex justify-center pt-4 border-t border-slate-200 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        className="gap-2"
                        data-testid="back-to-top-btn"
                      >
                        <ArrowUp className="h-4 w-4" />
                        Torna su
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Reject reason dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(open) => { if (!open) { setRejectTarget(null); setRejectReason(""); } }}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Rifiuta richiesta</DialogTitle>
            <DialogDescription>
              Stai per rifiutare la richiesta di {rejectTarget?.user_nome ? `${rejectTarget.user_nome} ${rejectTarget.user_cognome || ""}` : "questo dipendente"}. Puoi indicare una motivazione (opzionale) — verrà mostrata al dipendente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <Label htmlFor="reject-reason-dash">Eventuale motivazione del rifiuto</Label>
            <Textarea
              id="reject-reason-dash"
              placeholder="Es: in quel periodo siamo già scoperti..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              data-testid="reject-reason-input"
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => { setRejectTarget(null); setRejectReason(""); }}
              disabled={rejecting}
              data-testid="reject-cancel-btn"
            >
              Annulla
            </Button>
            <Button
              onClick={handleConfirmReject}
              disabled={rejecting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="reject-confirm-btn"
            >
              {rejecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
              Rifiuta richiesta
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}